import React, { useRef, useState, useEffect } from 'react';
import html2canvas from 'html2canvas';
import TradeCard from '../TradeCard/TradeCard';
import { trackModalOpen, trackModalClose, trackFeatureUsage } from '../../utils/analytics';
import './ShareModal.css';

function ShareModal({ trade, markPrice, onClose }) {
  const cardRef = useRef(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [isGenerating, setIsGenerating] = useState(true);
  const [copyStatus, setCopyStatus] = useState('');
  const [canShare, setCanShare] = useState(false);
  const [canCopyImage, setCanCopyImage] = useState(false);

  // Track modal open on mount
  useEffect(() => {
    trackModalOpen('share_modal', { symbol: trade?.symbol });
  }, []);

  // Check API availability after mount
  useEffect(() => {
    // Mobile detection
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    // Web Share API check - only on mobile
    const hasShare = isMobile && 'share' in navigator && window.isSecureContext;
    setCanShare(hasShare);

    // Clipboard API for images check
    const hasClipboard = 'clipboard' in navigator &&
                         'write' in (navigator.clipboard || {}) &&
                         typeof ClipboardItem !== 'undefined';
    setCanCopyImage(hasClipboard);

    console.log('Is mobile:', isMobile);
    console.log('Share API available:', hasShare);
    console.log('Clipboard API available:', hasClipboard);
  }, []);

  // Generate image when modal opens
  useEffect(() => {
    const generateImage = async () => {
      if (!cardRef.current) {
        setIsGenerating(false);
        return;
      }

      try {
        // Wait for every <img> inside the card to actually finish loading.
        // setTimeout(500) was racy in Safari — it would snapshot a card
        // with broken/empty images and then refuse to export the canvas.
        const imgs = Array.from(cardRef.current.querySelectorAll('img'));
        await Promise.all(
          imgs.map((img) =>
            img.complete && img.naturalWidth > 0
              ? Promise.resolve()
              : new Promise((resolve) => {
                  const done = () => resolve();
                  img.addEventListener('load', done, { once: true });
                  img.addEventListener('error', done, { once: true });
                })
          )
        );
        // One frame for layout/paint to settle after images load.
        await new Promise((resolve) => requestAnimationFrame(() => resolve()));

        const canvas = await html2canvas(cardRef.current, {
          scale: 2,
          useCORS: true,
          // allowTaint:true previously caused Safari toDataURL() to throw
          // SecurityError on a tainted canvas. All images inside the card
          // are same-origin /assets/* so CORS is enough; no need to taint.
          allowTaint: false,
          backgroundColor: '#1a1a1a',
          logging: false,
        });

        const url = canvas.toDataURL('image/png');
        setImageUrl(url);
      } catch (error) {
        console.error('Error generating image:', error);
      }
      setIsGenerating(false);
    };

    generateImage();
  }, [trade, markPrice]);

  // Copy image to clipboard
  const handleCopy = async () => {
    if (!imageUrl) return;

    if (!canCopyImage) {
      setCopyStatus('Not supported');
      setTimeout(() => setCopyStatus(''), 2000);
      return;
    }

    try {
      // Convert data URL to blob
      const response = await fetch(imageUrl);
      const blob = await response.blob();

      // Copy to clipboard
      await navigator.clipboard.write([
        new ClipboardItem({
          'image/png': blob
        })
      ]);
      trackFeatureUsage('copy_trade_image', { symbol: trade?.symbol });
      setCopyStatus('Copied!');
      setTimeout(() => setCopyStatus(''), 2000);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      setCopyStatus('Failed');
      setTimeout(() => setCopyStatus(''), 2000);
    }
  };

  // Download image
  const handleDownload = () => {
    if (!imageUrl) return;

    trackFeatureUsage('download_trade_image', { symbol: trade?.symbol });
    const link = document.createElement('a');
    link.download = `trade-${trade.symbol}-${Date.now()}.png`;
    link.href = imageUrl;
    link.click();
  };

  // Web Share API (mobile)
  const handleWebShare = async () => {
    if (!imageUrl) return;

    try {
      // Convert data URL to blob
      const response = await fetch(imageUrl);
      const blob = await response.blob();

      // Create file from blob
      const file = new File([blob], `trade-${trade.symbol}.png`, { type: 'image/png' });

      // Check if we can share files
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        trackFeatureUsage('web_share_trade', { symbol: trade?.symbol, method: 'with_file' });
        await navigator.share({
          files: [file],
          title: `${trade.symbol} Trade`,
          text: `${trade.side === 'buy' ? 'LONG' : 'SHORT'} ${trade.symbol} - $${(trade.dollarSize / 1000000).toFixed(1)}M`
        });
      } else {
        // Fallback: share without file
        trackFeatureUsage('web_share_trade', { symbol: trade?.symbol, method: 'text_only' });
        await navigator.share({
          title: `${trade.symbol} Trade`,
          text: `${trade.side === 'buy' ? 'LONG' : 'SHORT'} ${trade.symbol} - $${(trade.dollarSize / 1000000).toFixed(1)}M`
        });
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error sharing:', error);
      }
    }
  };

  // Handle close with tracking
  const handleClose = () => {
    trackModalClose('share_modal');
    onClose();
  };

  // Close on backdrop click
  const handleBackdropClick = (e) => {
    if (e.target.className === 'share-modal-backdrop') {
      handleClose();
    }
  };

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div className="share-modal-backdrop" onClick={handleBackdropClick}>
      <div className="share-modal">
        <button className="share-modal-close" onClick={handleClose}>
          ✕
        </button>

        <h2 className="share-modal-title">Share Trade</h2>

        {/* Hidden card for rendering */}
        <div className="share-modal-card-container" style={{ position: 'absolute', left: '-9999px' }}>
          <TradeCard ref={cardRef} trade={trade} markPrice={markPrice} />
        </div>

        {/* Display generated image */}
        <div className="share-modal-preview">
          {isGenerating ? (
            <div className="share-modal-loading">
              <div className="share-modal-spinner"></div>
              <span>Generating image...</span>
            </div>
          ) : imageUrl ? (
            <img src={imageUrl} alt="Trade Card" className="share-modal-image" />
          ) : (
            <div className="share-modal-error">Failed to generate image</div>
          )}
        </div>

        {/* Action buttons */}
        <div className="share-modal-actions">
          <button
            className="share-modal-btn share-modal-btn-copy"
            onClick={handleCopy}
            disabled={isGenerating || !imageUrl}
          >
            {copyStatus || 'Copy'}
          </button>
          <button
            className="share-modal-btn share-modal-btn-download"
            onClick={handleDownload}
            disabled={isGenerating || !imageUrl}
          >
            Save
          </button>
          {canShare && (
            <button
              className="share-modal-btn share-modal-btn-share"
              onClick={handleWebShare}
              disabled={isGenerating || !imageUrl}
            >
              Share
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ShareModal;
